import { useState, useEffect } from 'react';
import { Globe } from 'lucide-react';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import { domainsApi } from '@/lib/api';
import { toast } from 'sonner';

export const DomainFormModal = ({ isOpen, onClose, clientId, domain, options, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    domain_name: '',
    registrar: '20i',
    renewal_owner: 'Boxit In-house',
    renewal_date: '',
    auto_renew: true,
    dns_provider: '',
    hosting_type: 'Boxit 2.0',
    website_url: '',
    status: 'Live',
    notes: '',
  });

  useEffect(() => {
    if (domain) {
      setFormData({
        domain_name: domain.domain_name || '',
        registrar: domain.registrar || '20i',
        renewal_owner: domain.renewal_owner || 'Boxit In-house',
        renewal_date: domain.renewal_date || '',
        auto_renew: domain.auto_renew ?? true,
        dns_provider: domain.dns_provider || '',
        hosting_type: domain.hosting_type || 'Boxit 2.0',
        website_url: domain.website_url || '',
        status: domain.status || 'Live',
        notes: domain.notes || '',
      });
    } else {
      setFormData({
        domain_name: '',
        registrar: '20i',
        renewal_owner: 'Boxit In-house',
        renewal_date: '',
        auto_renew: true,
        dns_provider: '',
        hosting_type: 'Boxit 2.0',
        website_url: '',
        status: 'Live',
        notes: '',
      });
    }
  }, [domain, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (domain) {
        await domainsApi.update(domain.id, formData);
        toast.success('Domain updated');
      } else {
        await domainsApi.create(clientId, formData);
        toast.success('Domain added');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to save domain');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="domain-form-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-indigo-600" />
            {domain ? 'Edit Domain' : 'Add Domain'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="domain_name">Domain Name *</Label>
              <Input
                id="domain_name"
                value={formData.domain_name}
                onChange={(e) => setFormData({ ...formData, domain_name: e.target.value })}
                placeholder="example.co.uk"
                required
                data-testid="domain-name-input"
              />
            </div>

            <div>
              <Label htmlFor="registrar">Registrar</Label>
              <Select 
                value={formData.registrar} 
                onValueChange={(v) => setFormData({ ...formData, registrar: v })}
              >
                <SelectTrigger data-testid="registrar-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(options?.registrar || ['20i', 'Clook', 'GoDaddy', 'Namecheap', 'Cloudflare', 'Other']).map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="renewal_owner">Renewal Owner</Label>
              <Select 
                value={formData.renewal_owner} 
                onValueChange={(v) => setFormData({ ...formData, renewal_owner: v })}
              >
                <SelectTrigger data-testid="renewal-owner-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(options?.renewal_owner || ['Boxit In-house', 'Client-managed']).map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="renewal_date">Renewal Date</Label>
              <Input
                id="renewal_date"
                type="date"
                value={formData.renewal_date}
                onChange={(e) => setFormData({ ...formData, renewal_date: e.target.value })}
                data-testid="renewal-date-input"
              />
            </div>

            <div>
              <Label htmlFor="status">Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(v) => setFormData({ ...formData, status: v })}
              >
                <SelectTrigger data-testid="domain-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(options?.domain_status || ['Live', 'Redirected', 'Parked', 'Cancelled']).map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="hosting_type">Hosting Type</Label>
              <Select 
                value={formData.hosting_type} 
                onValueChange={(v) => setFormData({ ...formData, hosting_type: v })}
              >
                <SelectTrigger data-testid="hosting-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(options?.hosting_type || ['RapidWeaver Legacy', 'Boxit 2.0', 'Other']).map((h) => (
                    <SelectItem key={h} value={h}>{h}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="dns_provider">DNS Provider</Label>
              <Input
                id="dns_provider"
                value={formData.dns_provider}
                onChange={(e) => setFormData({ ...formData, dns_provider: e.target.value })}
                placeholder="e.g. Cloudflare"
                data-testid="dns-provider-input"
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="website_url">Website URL</Label>
              <Input
                id="website_url"
                value={formData.website_url}
                onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                placeholder="https://example.co.uk"
                data-testid="website-url-input"
              />
            </div>

            <div className="col-span-2 flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <Label htmlFor="auto_renew" className="cursor-pointer">Auto-Renew Enabled</Label>
              <Switch
                id="auto_renew"
                checked={formData.auto_renew}
                onCheckedChange={(checked) => setFormData({ ...formData, auto_renew: checked })}
                data-testid="auto-renew-switch"
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={3}
                data-testid="domain-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-domain-btn"
            >
              {isLoading ? 'Saving...' : (domain ? 'Update Domain' : 'Add Domain')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
